import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Popper } from "@mui/material";
import { MagnifyingGlassIcon, PlusIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import {
  getGlobalProductsList,
  aggsFilterCategory,
  changeStockStatus,
  getGlobalProductsListV2,
} from "../../../../../config/Services";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";
import PosCustomFilterV3 from "../../../../../customComponents/PosCustomFilterV3/PosCustomFilterV3";
import "./MenuAvailabilityDrawer.css";
import PauseOnlineOrdersModal from "./PauseOnlineOrdersModal";
import { getStoreCurrencySymbol } from "../../../../../utils/currency";

const PAGE_SIZE = 10;

const XIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const MenuAvailabilityDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("menu"); // "menu" or "addons"
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [products, setProducts] = useState([]);
  const [availabilityFilter, setAvailabilityFilter] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const productsContainerRef = useRef(null);

  // Refs to track current values for scroll handler
  const currentPageRef = useRef(currentPage);
  const hasMoreRef = useRef(hasMore);
  const loadingMoreRef = useRef(loadingMore);

  // Keep refs in sync with state
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  // Variant Modal State
  const [tooltipProductId, setTooltipProductId] = useState(null);
  const [tooltipVariantKey, setTooltipVariantKey] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [popoverProduct, setPopoverProduct] = useState(null);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [togglingChannels, setTogglingChannels] = useState(new Set());
  const [checkedChannelKeys, setCheckedChannelKeys] = useState(new Set());

  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  const branchSubType = localStorage.getItem("branchSubType");
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
  const showSnackbar = useSnackbar();

  // Sales channels
  const salesChannels = [
    { id: "DINE_IN", name: "Dine In" },
    { id: "TAKE_AWAY", name: "Take Away" },
    { id: "DELIVERY", name: "Delivery" },
    { id: "SWIGGY", name: "Swiggy" },
    { id: "ZOMATO", name: "Zomato" },
    { id: "WHATSAPP", name: "WhatsApp" },
  ];

  // All sales channel IDs for API payload
  const salesChannelIds = salesChannels.map((channel) => channel.id);

  // Availability filter options
  const availabilityOptions = [
    { value: "ACTIVE", label: "Available" },
    { value: "IN_ACTIVE", label: "Unavailable" },
  ];

  // Only one option can be selected at a time
  const handleAvailabilityFilterChange = (selected) => {
    const lastSelected = selected[selected.length - 1];
    setAvailabilityFilter(lastSelected ? [lastSelected] : []);
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch categories
  useEffect(() => {
    if (!open) return;

    setCategoriesLoading(true);
    const payload = {
      page: 1,
      pageSize: 50,
      storeIds: [orgId],
      storeLocations: [locId],
      sortByUpdatedAt: "DESC",
      productTypes: ["MENU"],
      salesChannels: salesChannelIds,
    };

    aggsFilterCategory(payload)
      .then((res) => {
        const categoryData = res?.data?.data?.data || [];
        setCategories(categoryData);
        setCategoriesLoading(false);
      })
      .catch((err) => {
        showSnackbar("Error fetching categories", "error");
        setCategoriesLoading(false);
      });
  }, [open, orgId, locId]);

  // Reset pagination when filters change
  useEffect(() => {
    if (!open) return;

    setProducts([]);
    setCurrentPage(1);
    setHasMore(true);
    hasMoreRef.current = true;
    currentPageRef.current = 1;
    fetchProducts(1, true);
  }, [open, selectedCategory, debouncedSearchQuery, activeTab, availabilityFilter]);

  const fetchProducts = (page = 1, isReset = false) => {
    if (isReset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    const payload = {
      page,
      pageSize: PAGE_SIZE,
      storeLocations: [locId],
      storeIds: [orgId],
      productTypes: activeTab === "menu" ? ["MENU"] : ["ADD_ON"],
      salesChannels: salesChannelIds,
      ...(selectedCategory !== "all" && {
        appCategories: {
          categoryLevel1: [selectedCategory],
        },
      }),
      ...(debouncedSearchQuery && { query: debouncedSearchQuery }),
      ...(availabilityFilter.length > 0 && { productStatus: availabilityFilter }),
    };

    getGlobalProductsListV2(payload)
      .then((res) => {
        const productData = res?.data?.data?.data?.data || [];
        const totalRecords = res?.data?.data?.data?.totalRecords || 0;

        if (isReset) {
          setProducts(productData);
          // Check if there are more products to load
          setHasMore(productData.length < totalRecords);
          hasMoreRef.current = productData.length < totalRecords;
        } else {
          setProducts((prev) => {
            const newProducts = [...prev, ...productData];
            setHasMore(newProducts.length < totalRecords);
            hasMoreRef.current = newProducts.length < totalRecords;
            return newProducts;
          });
        }

        setCurrentPage(page);
        currentPageRef.current = page;
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      })
      .catch((err) => {
        showSnackbar("Error fetching products", "error");
        setLoading(false);
        setLoadingMore(false);
      });
  };

  // Handle scroll for infinite pagination
  const handleScroll = () => {
    if (!productsContainerRef.current || loadingMoreRef.current || !hasMoreRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = productsContainerRef.current;

    // Load more when user scrolls to bottom (with 100px threshold)
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      fetchProductsNextPage(currentPageRef.current + 1);
    }
  };

  // Separate function for loading next page to avoid stale closures
  const fetchProductsNextPage = (page) => {
    setLoadingMore(true);
    loadingMoreRef.current = true;

    const payload = {
      page,
      pageSize: PAGE_SIZE,
      storeLocations: [locId],
      storeIds: [orgId],
      productTypes: activeTab === "menu" ? ["MENU"] : ["ADD_ON"],
      salesChannels: salesChannelIds,
      ...(selectedCategory !== "all" && {
        appCategories: {
          categoryLevel1: [selectedCategory],
        },
      }),
      ...(debouncedSearchQuery && { query: debouncedSearchQuery }),
      ...(availabilityFilter.length > 0 && { productStatus: availabilityFilter }),
    };

    getGlobalProductsListV2(payload)
      .then((res) => {
        const productData = res?.data?.data?.data?.data || [];
        const totalRecords = res?.data?.data?.data?.totalRecords || 0;

        setProducts((prev) => {
          const newProducts = [...prev, ...productData];
          const hasMoreData = newProducts.length < totalRecords;
          setHasMore(hasMoreData);
          hasMoreRef.current = hasMoreData;
          return newProducts;
        });

        setCurrentPage(page);
        currentPageRef.current = page;
        setLoadingMore(false);
        loadingMoreRef.current = false;
      })
      .catch((err) => {
        showSnackbar("Error fetching products", "error");
        setLoadingMore(false);
        loadingMoreRef.current = false;
      });
  };

  const handleProductToggle = (product) => {
    setSelectedProduct(product);
    setTogglingChannels(new Set());
    setVariantModalOpen(true);
  };

  const handleChannelToggle = async (variantId, channelName, currentStatus) => {
    const toggleKey = `${variantId}-${channelName}`;
    if (togglingChannels.has(toggleKey)) return;

    setTogglingChannels((prev) => new Set([...prev, toggleKey]));

    const newStatus = currentStatus === "IN_STOCK" ? "OUT_OF_STOCK" : "IN_STOCK";

    // Optimistically update UI
    setSelectedProduct((prev) => {
      if (!prev) return prev;
      const updatedVariants = prev.variants.map((v) => {
        if (v.variantId !== variantId) return v;
        const updatedChannels = (v.externalSalesChannels || []).map((ch) =>
          ch.salesChannelName === channelName ? { ...ch, stockStatus: newStatus } : ch
        );
        return { ...v, externalSalesChannels: updatedChannels };
      });
      return { ...prev, variants: updatedVariants };
    });

    try {
      const payload = {
        productId: selectedProduct?.id,
        orgId,
        locationId: locId,
        variantStockRequests: [
          {
            variantId,
            salesChannel: [channelName],
            markAll: false,
          },
        ],
        stockStatus: newStatus,
        markAll: false,
        updatedBy: retailUserDetails?.uidx,
        updatedByName: retailUserDetails?.firstName,
      };

      const response = await changeStockStatus(payload);

      if (response?.data?.status === "SUCCESS") {
        // Update the product in the products list too
        setProducts((prev) =>
          prev.map((p) => {
            if (p.id !== selectedProduct?.id) return p;
            const updatedVariants = p.variants.map((v) => {
              if (v.variantId !== variantId) return v;
              const updatedChannels = (v.externalSalesChannels || []).map((ch) =>
                ch.salesChannelName === channelName ? { ...ch, stockStatus: newStatus } : ch
              );
              return { ...v, externalSalesChannels: updatedChannels };
            });
            return { ...p, variants: updatedVariants };
          })
        );
        showSnackbar(
          `Marked as ${newStatus === "IN_STOCK" ? "In Stock" : "Out of Stock"}`,
          "success"
        );
      } else {
        // Revert on failure
        setSelectedProduct((prev) => {
          if (!prev) return prev;
          const revertedVariants = prev.variants.map((v) => {
            if (v.variantId !== variantId) return v;
            const revertedChannels = (v.externalSalesChannels || []).map((ch) =>
              ch.salesChannelName === channelName ? { ...ch, stockStatus: currentStatus } : ch
            );
            return { ...v, externalSalesChannels: revertedChannels };
          });
          return { ...prev, variants: revertedVariants };
        });
        showSnackbar("Error updating availability", "error");
      }
    } catch (err) {
      // Revert on error
      setSelectedProduct((prev) => {
        if (!prev) return prev;
        const revertedVariants = prev.variants.map((v) => {
          if (v.variantId !== variantId) return v;
          const revertedChannels = (v.externalSalesChannels || []).map((ch) =>
            ch.salesChannelName === channelName ? { ...ch, stockStatus: currentStatus } : ch
          );
          return { ...v, externalSalesChannels: revertedChannels };
        });
        return { ...prev, variants: revertedVariants };
      });
      showSnackbar("Error updating availability", "error");
    } finally {
      setTogglingChannels((prev) => {
        const next = new Set(prev);
        next.delete(toggleKey);
        return next;
      });
    }
  };

  const handleMarkAll = async (newStatus) => {
    if (!selectedProduct) return;

    const variantStockRequests = [];
    (selectedProduct?.variants || []).forEach((variant) => {
      const channels = (variant?.externalSalesChannels || [])
        .filter((ch) => ch?.stockStatus !== newStatus)
        .map((ch) => ch?.salesChannelName);
      if (channels.length > 0) {
        variantStockRequests.push({
          variantId: variant?.variantId,
          salesChannel: channels,
          markAll: false,
        });
      }
    });

    if (variantStockRequests.length === 0) {
      showSnackbar(
        `All channels are already ${newStatus === "IN_STOCK" ? "In Stock" : "Out of Stock"}`,
        "info"
      );
      return;
    }

    // Mark all toggle keys as loading
    const allKeys = [];
    variantStockRequests.forEach((req) => {
      req.salesChannel.forEach((ch) => allKeys.push(`${req.variantId}-${ch}`));
    });
    setTogglingChannels((prev) => new Set([...prev, ...allKeys]));

    // Optimistically update UI
    setSelectedProduct((prev) => {
      if (!prev) return prev;
      const updatedVariants = prev.variants.map((v) => ({
        ...v,
        externalSalesChannels: (v.externalSalesChannels || []).map((ch) => ({
          ...ch,
          stockStatus: newStatus,
        })),
      }));
      return { ...prev, variants: updatedVariants };
    });

    try {
      const payload = {
        productId: selectedProduct?.id,
        orgId,
        locationId: locId,
        variantStockRequests,
        stockStatus: newStatus,
        markAll: false,
        updatedBy: retailUserDetails?.uidx,
        updatedByName: retailUserDetails?.firstName,
      };

      const response = await changeStockStatus(payload);

      if (response?.data?.status === "SUCCESS") {
        setProducts((prev) =>
          prev.map((p) => {
            if (p.id !== selectedProduct?.id) return p;
            const updatedVariants = p.variants.map((v) => ({
              ...v,
              externalSalesChannels: (v.externalSalesChannels || []).map((ch) => ({
                ...ch,
                stockStatus: newStatus,
              })),
            }));
            return { ...p, variants: updatedVariants };
          })
        );
        showSnackbar(
          `All channels marked as ${newStatus === "IN_STOCK" ? "In Stock" : "Out of Stock"}`,
          "success"
        );
      } else {
        // Revert
        setProducts([]);
        setCurrentPage(1);
        setHasMore(true);
        hasMoreRef.current = true;
        currentPageRef.current = 1;
        fetchProducts(1, true);
        showSnackbar("Error updating availability", "error");
      }
    } catch (err) {
      showSnackbar("Error updating availability", "error");
    } finally {
      setTogglingChannels((prev) => {
        const next = new Set(prev);
        allKeys.forEach((k) => next.delete(k));
        return next;
      });
    }
  };

  const getChannelDisplayName = (channelName) => {
    const match = salesChannels.find((sc) => sc.id === channelName);
    return match?.name || channelName;
  };

  const getProductImage = (product) => {
    const variant = product?.variants?.[0];
    return (
      variant?.images?.front ||
      variant?.images?.back ||
      "https://tse4.mm.bing.net/th?id=OIP.mPwk4FItjNeb2Oaq8F1u9QHaGE&pid=Api&P=0"
    );
  };

  const getProductPrice = (product) => {
    const variant = product?.variants?.[0];
    const dineInChannel = variant?.externalSalesChannels?.find(
      (ch) => ch?.salesChannelName === "DINE_IN"
    );
    return dineInChannel?.salePrice ?? variant?.inventorySync?.sellingPrice ?? 0;
  };

  const getOutOfStockVariants = (product) => {
    const variants = product?.variants || [];
    const result = [];
    variants.forEach((variant) => {
      const channels = variant?.externalSalesChannels || [];
      const outChannels = channels
        .filter((ch) => ch?.stockStatus === "OUT_OF_STOCK")
        .map((ch) => {
          const match = salesChannels.find((sc) => sc.id === ch?.salesChannelName);
          return match?.name || ch?.salesChannelName;
        });
      if (outChannels.length > 0) {
        result.push({ variantName: variant?.name, channels: outChannels });
      }
    });
    return result;
  };

  const isProductAvailable = (product) => {
    const variants = product?.variants || [];
    return variants.every((variant) => {
      const channels = variant?.externalSalesChannels || [];
      if (channels.length === 0) return variant?.stockStatus === "IN_STOCK";
      return channels.every((ch) => ch?.stockStatus === "IN_STOCK");
    });
  };

  const getProductCategory = (product) => {
    return product?.appCategories?.categoryLevel1 || product?.category || "Uncategorized";
  };

  const filteredProducts = useMemo(() => {
    return products;
  }, [products]);

  const toggleChannelCheck = (key) => {
    setCheckedChannelKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleConfirmChanges = async () => {
    if (!selectedProduct) return;

    if (checkedChannelKeys.size === 0) {
      await handleMarkAll("OUT_OF_STOCK");
      setVariantModalOpen(false);
      setCheckedChannelKeys(new Set());
      return;
    }

    // Split checked channels by their desired new status (toggle current)
    const toEnable = {}; // variantId → [channelName] for OUT_OF_STOCK → IN_STOCK
    const toDisable = {}; // variantId → [channelName] for IN_STOCK → OUT_OF_STOCK
    const enableKeys = new Set();
    const disableKeys = new Set();

    (selectedProduct?.variants || []).forEach((variant) => {
      (variant?.externalSalesChannels || []).forEach((ch) => {
        const key = `${variant?.variantId}-${ch?.salesChannelName}`;
        if (!checkedChannelKeys.has(key)) return;
        const vid = variant?.variantId;
        if (ch?.stockStatus === "OUT_OF_STOCK") {
          if (!toEnable[vid]) toEnable[vid] = [];
          toEnable[vid].push(ch?.salesChannelName);
          enableKeys.add(key);
        } else {
          if (!toDisable[vid]) toDisable[vid] = [];
          toDisable[vid].push(ch?.salesChannelName);
          disableKeys.add(key);
        }
      });
    });

    const allKeys = [...checkedChannelKeys];
    setTogglingChannels((prev) => new Set([...prev, ...allKeys]));

    // Optimistic update — flip each checked channel
    setSelectedProduct((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        variants: prev?.variants?.map((v) => ({
          ...v,
          externalSalesChannels: (v?.externalSalesChannels || []).map((ch) => {
            const key = `${v?.variantId}-${ch?.salesChannelName}`;
            if (!checkedChannelKeys.has(key)) return ch;
            return {
              ...ch,
              stockStatus: ch?.stockStatus === "OUT_OF_STOCK" ? "IN_STOCK" : "OUT_OF_STOCK",
            };
          }),
        })),
      };
    });

    const buildRequests = (byVariant) =>
      Object.entries(byVariant)?.map(([variantId, salesChannel]) => ({
        variantId,
        salesChannel,
        markAll: false,
      }));

    const callApi = async (byVariant, newStatus) => {
      const variantStockRequests = buildRequests(byVariant);
      if (variantStockRequests?.length === 0) return true;
      const payload = {
        productId: selectedProduct?.id,
        orgId,
        locationId: locId,
        variantStockRequests,
        stockStatus: newStatus,
        markAll: false,
        updatedBy: retailUserDetails?.uidx,
        updatedByName: retailUserDetails?.firstName,
      };
      const response = await changeStockStatus(payload);
      return response?.data?.status === "SUCCESS";
    };

    try {
      const [enableOk, disableOk] = await Promise.all([
        callApi(toEnable, "IN_STOCK"),
        callApi(toDisable, "OUT_OF_STOCK"),
      ]);

      if (enableOk && disableOk) {
        setProducts((prev) =>
          prev?.map((p) => {
            if (p.id !== selectedProduct?.id) return p;
            return {
              ...p,
              variants: p?.variants?.map((v) => ({
                ...v,
                externalSalesChannels: (v?.externalSalesChannels || []).map((ch) => {
                  const key = `${v?.variantId}-${ch?.salesChannelName}`;
                  if (enableKeys.has(key)) return { ...ch, stockStatus: "IN_STOCK" };
                  if (disableKeys.has(key)) return { ...ch, stockStatus: "OUT_OF_STOCK" };
                  return ch;
                }),
              })),
            };
          })
        );
        showSnackbar("Availability updated successfully", "success");
        setCheckedChannelKeys(new Set());
        setVariantModalOpen(false);
      } else {
        setProducts([]);
        setCurrentPage(1);
        setHasMore(true);
        hasMoreRef.current = true;
        currentPageRef.current = 1;
        fetchProducts(1, true);
        showSnackbar("Error updating availability", "error");
      }
    } catch (err) {
      showSnackbar("Error updating availability", "error");
    } finally {
      setTogglingChannels((prev) => {
        const next = new Set(prev);
        allKeys.forEach((k) => next.delete(k));
        return next;
      });
    }
  };

  if (!open) return null;

  return (
    <div className="menu-availability-overlay-wrapper">
      {/* Overlay Backdrop */}
      <div className="menu-availability-overlay-backdrop" onClick={onClose} />

      {/* Drawer */}
      <div className="menu-availability-drawer">
        {/* Top Bar / Header */}
        <div className="menu-availability-header">
          <h1 className="menu-availability-title">Menu Availability</h1>
          <div className="menu-availability-header-actions">
            {/* Availability Filter */}
            <PosCustomFilterV3
              options={availabilityOptions}
              selectedValues={availabilityFilter}
              onChange={handleAvailabilityFilterChange}
              styleProps={{
                buttonLabel: "Availability",
                buttonRadius: "12px",
                buttonShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                buttonBorder: "1px solid #e2e8f0",
                badgePosition: "top-right",
              }}
            />
            <button
              className="menu-availability-add-btn"
              onClick={() => {
                onClose();
                navigate("/settings/create-menu");
              }}
            >
              <PlusIcon className="menu-availability-add-icon" />
              Add Menu
            </button>
            <button
              className="menu-availability-close-btn"
              onClick={onClose}
              data-testid="button-close-menu-toggle"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="menu-availability-close-icon"
                aria-hidden="true"
              >
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Sub-header / Filters Bar */}
        <div className="menu-availability-subbar">
          <div className="menu-availability-subbar-left">
            {/* Menu/Addons Tabs */}
            <div className="menu-availability-tabs-container">
              <button
                className={`menu-availability-tab-btn ${activeTab === "menu" ? "active" : ""}`}
                onClick={() => setActiveTab("menu")}
                data-testid="button-menu-type-items"
              >
                Menu Items
              </button>
              <button
                className={`menu-availability-tab-btn ${activeTab === "addons" ? "active" : ""}`}
                onClick={() => setActiveTab("addons")}
                data-testid="button-menu-type-addons"
              >
                Add-ons
              </button>
            </div>

            <div className="menu-availability-subbar-divider"></div>

            {/* Restaurant Online Status Switch */}
            <div className="menu-availability-status-switch-wrapper">
              <div className="menu-availability-status-switch-info">
                <p className="menu-availability-status-switch-title">Restaurant Online</p>
                <p className="menu-availability-status-switch-subtitle">
                  Availability across all channels
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked="true"
                data-state="checked"
                value="on"
                className="menu-availability-status-toggle active"
                data-testid="switch-restaurant-online"
                onClick={() => setIsPauseModalOpen(true)}
              >
                <span className="menu-availability-status-toggle-knob active"></span>
              </button>
            </div>
          </div>

          <div className="menu-availability-subbar-right">
            {/* Add Menu Button */}
            {/* <button
              className="menu-availability-add-btn"
              onClick={() => {
                onClose();
                navigate("/settings/create-menu-form");
              }}
            >
              <PlusIcon className="menu-availability-add-icon" />
              Add Menu
            </button> */}

            {/* Search Input Container */}
            <div className="menu-availability-search-wrapper">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="menu-availability-search-svg"
                aria-hidden="true"
              >
                <path d="m21 21-4.34-4.34"></path>
                <circle cx="11" cy="11" r="8"></circle>
              </svg>
              <input
                className="menu-availability-search-input"
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="input-menu-search"
              />
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="menu-availability-content">
          {/* Categories Sidebar */}
          <div className="menu-availability-categories">
            {categoriesLoading ? (
              <div className="menu-availability-loading">
                <CircularProgress size={24} sx={{ color: "#0860E6" }} />
              </div>
            ) : (
              <>
                <button
                  className={`menu-availability-category-item ${
                    selectedCategory === "all" ? "active" : ""
                  }`}
                  onClick={() => setSelectedCategory("all")}
                >
                  <span>All</span>
                  {selectedCategory === "all" && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="menu-availability-category-chevron"
                      aria-hidden="true"
                    >
                      <path d="m9 18 6-6-6-6"></path>
                    </svg>
                  )}
                </button>
                {categories?.map((category) => (
                  <button
                    key={category?.key}
                    className={`menu-availability-category-item ${
                      selectedCategory === category?.key ? "active" : ""
                    }`}
                    onClick={() => setSelectedCategory(category?.key)}
                  >
                    <span>{category?.key}</span>
                    {selectedCategory === category?.key && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="menu-availability-category-chevron"
                        aria-hidden="true"
                      >
                        <path d="m9 18 6-6-6-6"></path>
                      </svg>
                    )}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Products List */}
          <div
            className="menu-availability-products"
            ref={productsContainerRef}
            onScroll={handleScroll}
          >
            {loading ? (
              <div className="menu-availability-loading">
                <CircularProgress size={40} sx={{ color: "#0860E6" }} />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="menu-availability-no-results">
                <p>No items found</p>
              </div>
            ) : (
              <div className="menu-availability-products-list">
                {filteredProducts.map((product) => (
                  <div key={product?.id} className="menu-availability-product-row">
                    <div className="menu-availability-product-image-container">
                      <img
                        src={getProductImage(product)}
                        alt={product?.name}
                        className="menu-availability-product-image"
                      />
                    </div>
                    <div className="menu-availability-product-details">
                      <div className="menu-availability-product-title-row">
                        <span
                          className="mad-info-tooltip"
                          onMouseEnter={() => setTooltipProductId(product?.id)}
                          onMouseLeave={() => setTooltipProductId(null)}
                        >
                          <InformationCircleIcon
                            style={{ width: 16, height: 16, color: "#6b7280", flexShrink: 0 }}
                          />
                          {tooltipProductId === product?.id && (
                            <div className="mad-info-tooltip-box">{product?.name}</div>
                          )}
                        </span>
                        <h3 className="menu-availability-product-name">{product?.name}</h3>
                        <span className="menu-availability-product-badge">
                          {getProductCategory(product)}
                        </span>
                      </div>
                      <p className="menu-availability-product-price">{getStoreCurrencySymbol()}{getProductPrice(product)}</p>
                    </div>
                    <div className="menu-availability-product-status-container">
                      {/* We moved the badge to the subtext area below */}

                      <div className="menu-availability-product-status-text">
                        <span
                          className={`menu-availability-status-label-text ${
                            isProductAvailable(product) ? "on-sale" : "unavailable"
                          }`}
                        >
                          {isProductAvailable(product) ? "On Sale" : "Unavailable"}
                        </span>
                        {isProductAvailable(product) ? (
                          <span className="menu-availability-status-subtext">
                            All Channels Active
                          </span>
                        ) : (
                          <span
                            className="menu-availability-oos-badge"
                            onMouseEnter={(e) => {
                              setPopoverProduct(product);
                              setPopoverAnchorEl(e.currentTarget);
                            }}
                            onMouseLeave={() => {
                              setPopoverAnchorEl(null);
                              setPopoverProduct(null);
                            }}
                            onClick={(e) => {
                              setPopoverProduct(product);
                              setPopoverAnchorEl(e.currentTarget);
                            }}
                          >
                            Disabled
                            <InformationCircleIcon
                              style={{ width: 12, height: 12, color: "inherit", marginLeft: 4 }}
                            />
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={isProductAvailable(product)}
                        className={`menu-availability-switch-button ${
                          isProductAvailable(product) ? "active" : "inactive"
                        }`}
                        onClick={() => handleProductToggle(product)}
                      >
                        <span
                          className={`menu-availability-switch-button-knob ${
                            isProductAvailable(product) ? "active" : "inactive"
                          }`}
                        ></span>
                      </button>
                    </div>
                  </div>
                ))}
                {/* Loading more indicator */}
                {loadingMore && (
                  <div className="menu-availability-loading-more">
                    <CircularProgress size={24} sx={{ color: "#0860E6" }} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <PauseOnlineOrdersModal open={isPauseModalOpen} onClose={() => setIsPauseModalOpen(false)} />

      {/* Variant Modal */}
      {variantModalOpen && selectedProduct && (
        <>
          <div
            className="mad-dialog-overlay"
            onClick={() => {
              setVariantModalOpen(false);
              setCheckedChannelKeys(new Set());
            }}
          />
          <div className="mad-dialog">
            {/* Header */}
            <div className="mad-dialog-header">
              <h2 className="mad-dialog-title">Turn Off Availability</h2>
            </div>

            {/* Body */}
            <div className="mad-dialog-body">
              <div className="mad-dialog-section">
                <h4 className="mad-section-heading">Select Channels to Disable</h4>
                <div className="mad-variant-list">
                  {selectedProduct?.variants?.map((variant) => {
                    const channels = variant?.externalSalesChannels || [];
                    if (channels?.length === 0) return null;
                    const multiVariant =
                      (selectedProduct?.variants?.filter(
                        (v) => (v?.externalSalesChannels || []).length > 0
                      ).length || 0) > 1;

                    return (
                      <div key={variant?.variantId} className="mad-vs-group">
                        {multiVariant && (
                          <div className="mad-vs-header">
                            <span className="mad-vs-name">
                              {variant?.name || selectedProduct?.name}
                            </span>
                          </div>
                        )}
                        {channels?.map((ch) => {
                          const channelKey = `${variant?.variantId}-${ch?.salesChannelName}`;
                          const isChecked = checkedChannelKeys.has(channelKey);
                          const isInStock = ch?.stockStatus === "IN_STOCK";
                          const isToggling = togglingChannels.has(channelKey);
                          return (
                            <div
                              key={ch?.salesChannelName}
                              className={`mad-ch-row${isChecked ? " mad-ch-row--checked" : ""}${
                                isToggling ? " mad-ch-row--toggling" : ""
                              }`}
                              onClick={() => !isToggling && toggleChannelCheck(channelKey)}
                            >
                              <span
                                className={`mad-vr-checkbox${
                                  isChecked ? " mad-vr-checkbox--checked" : ""
                                }`}
                              >
                                {isChecked && (
                                  <svg
                                    width="10"
                                    height="10"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                  >
                                    <path d="M20 6 9 17l-5-5" />
                                  </svg>
                                )}
                              </span>

                              <div className="mad-ch-name-price-container">
                                <span className="mad-ch-name">
                                  {getChannelDisplayName(ch?.salesChannelName)}
                                </span>
                                {ch?.salePrice != null && (
                                  <span className="mad-ch-price">{`(${getStoreCurrencySymbol()}${ch.salePrice})`}</span>
                                )}
                              </div>

                              <span
                                className={`mad-ch-status ${
                                  isInStock ? "in-stock" : "out-of-stock"
                                }`}
                              >
                                {isInStock ? "On Sale" : "Disabled"}
                              </span>
                              {isToggling && (
                                <CircularProgress size={16} sx={{ color: "#0860E6" }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                <p className="mad-section-hint">
                  * If no channels are selected, all channels will be disabled.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="mad-dialog-footer">
              <button
                className="mad-footer-btn mad-footer-btn--cancel"
                onClick={() => {
                  setVariantModalOpen(false);
                  setCheckedChannelKeys(new Set());
                }}
              >
                Cancel
              </button>
              <button
                className="mad-footer-btn mad-footer-btn--confirm"
                onClick={handleConfirmChanges}
                disabled={togglingChannels?.size > 0}
              >
                Confirm Changes
              </button>
            </div>

            {/* Absolute close button */}
            <button
              type="button"
              className="mad-dialog-close-abs"
              onClick={() => {
                setVariantModalOpen(false);
                setCheckedChannelKeys(new Set());
              }}
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </>
      )}
      {/* Popper for Disabled Channels with Virtual Anchor for diagonal placement */}
      {popoverAnchorEl && (
        <Popper
          open={Boolean(popoverAnchorEl)}
          anchorEl={{
            getBoundingClientRect: () => {
              const rect = popoverAnchorEl.getBoundingClientRect();
              return {
                top: rect.top,
                bottom: rect.bottom,
                left: rect.left,
                right: rect.left, // Collapse width to its left edge
                width: 0,
                height: rect.height,
                x: rect.left,
                y: rect.top,
              };
            },
            nodeType: 1,
          }}
          placement="bottom-end"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
            {
              name: "preventOverflow",
              options: {
                padding: 16,
              },
            },
          ]}
          style={{ zIndex: 1300, pointerEvents: "none" }}
        >
          <div className="mad-popover-content">
            {popoverProduct &&
              getOutOfStockVariants(popoverProduct)?.map((v) => (
                <div key={v?.variantName} className="mad-popover-variant-row">
                  <span className="mad-popover-variant-name">{v?.variantName}</span>
                  <div className="mad-popover-channel-tags">
                    {v?.channels?.map((ch) => (
                      <span key={ch} className="mad-popover-channel-tag">
                        {ch}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </Popper>
      )}
    </div>
  );
};

export default MenuAvailabilityDrawer;
